// src/components/buyer-leads/BuyerNeedsForm.jsx
import React from "react";
import {
  PROPERTY_TYPE_OPTIONS,
  FINANCING_OPTIONS,
} from "../../utils/buyerLeadStages";
import { RATING_OPTIONS } from "../../utils/buyerActivity";

const Field = ({ label, children }) => (
  <div className="w-full">
    <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full text-sm rounded-lg border border-slate-300 focus:border-[#1E293B] outline-none px-3 py-2";

// Comma-separated text <-> string[] (used for Areas and Must-Haves).
const toCsv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
const fromCsv = (s) =>
  s.split(",").map((x) => x.trim()).filter(Boolean);

// Money input: shows "$500,000" (with $ and commas) while storing the raw
// number (null when empty). A type="number" field can't render $/commas, so
// we format a text field and strip non-digits on change.
const MoneyInput = ({ value, onChange, placeholder }) => {
  const display =
    value === null || value === undefined || value === ""
      ? ""
      : `$${Number(value).toLocaleString("en-US")}`;
  const handleChange = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    onChange(digits === "" ? null : Number(digits));
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      className={inputCls}
      value={display}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
};

// A comma-separated list input (Areas / Must-Haves / Tags). Holds the RAW typed
// text locally so commas — and the spaces after them — survive as you type.
// Deriving the input's value straight from the parsed array (toCsv) erased a
// trailing comma the instant it was typed, which made it impossible to start a
// second city. The parsed string[] is still committed upward on every change;
// the local text only re-syncs from the array while the field is unfocused
// (e.g. when a different lead loads into the form).
const CsvInput = ({ value, onChange, placeholder }) => {
  const [text, setText] = React.useState(() => toCsv(value));
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => {
    if (!focused) setText(toCsv(value));
  }, [value, focused]);
  return (
    <input
      type="text"
      className={inputCls}
      value={text}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        onChange(fromCsv(e.target.value));
      }}
    />
  );
};

export default function BuyerNeedsForm({
  value,
  onChange,
  leadSources = [],
  agents = [],
}) {
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const setNum = (k) => (e) =>
    onChange({ [k]: e.target.value === "" ? null : Number(e.target.value) });

  const togglePropertyType = (t) => {
    const cur = Array.isArray(value.propertyTypes) ? value.propertyTypes : [];
    onChange({
      propertyTypes: cur.includes(t)
        ? cur.filter((x) => x !== t)
        : [...cur, t],
    });
  };

  return (
    <div className="space-y-5">
      {/* Core needs */}
      <div>
        <h4 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">
          Buyer Needs
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price Min">
            <MoneyInput value={value.priceMin} onChange={(v) => onChange({ priceMin: v })} placeholder="$450,000" />
          </Field>
          <Field label="Price Max">
            <MoneyInput value={value.priceMax} onChange={(v) => onChange({ priceMax: v })} placeholder="$525,000" />
          </Field>

          <Field label="Pre-Approval">
            <select className={inputCls} value={value.preApproval || ""} onChange={set("preApproval")}>
              <option value="">—</option>
              <option value="yes">Pre-Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="no">Not Yet</option>
            </select>
          </Field>
          <Field label="Pre-Approval Amount">
            <MoneyInput value={value.preApprovalAmount} onChange={(v) => onChange({ preApprovalAmount: v })} placeholder="$540,000" />
          </Field>

          <div className="col-span-2">
            <Field label="Desired Areas (comma-separated)">
              <CsvInput value={value.areas} onChange={(areas) => onChange({ areas })} placeholder="Westside, Maple Heights, Downtown" />
            </Field>
          </div>

          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1">Property Type</label>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPE_OPTIONS.map((t) => {
                const on = (value.propertyTypes || []).includes(t);
                return (
                  <button type="button" key={t} onClick={() => togglePropertyType(t)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border ${on ? "bg-slate-100 border-slate-300 text-linkColor" : "bg-white border-slate-300 text-slate-600"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Beds (min)">
            <input type="number" className={inputCls} value={value.bedsMin ?? ""} onChange={setNum("bedsMin")} placeholder="3" />
          </Field>
          <Field label="Baths (min)">
            <input type="number" className={inputCls} value={value.bathsMin ?? ""} onChange={setNum("bathsMin")} placeholder="2" />
          </Field>

          <Field label="Timeline">
            <select className={inputCls} value={value.timeline || ""} onChange={set("timeline")}>
              <option value="">—</option>
              <option value="asap">ASAP</option>
              <option value="30">~30 days</option>
              <option value="60">~60 days</option>
              <option value="90">~90 days</option>
              <option value="6mo_plus">6+ months</option>
            </select>
          </Field>
          <Field label="Lead Source">
            <select className={inputCls} value={value.leadSource || ""} onChange={set("leadSource")}>
              <option value="">—</option>
              {leadSources.map((s) => (
                <option key={s.id || s.docId} value={s.id || s.docId}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Rating (A/B/C)">
            <select className={inputCls} value={value.rating || ""} onChange={set("rating")}>
              <option value="">—</option>
              {RATING_OPTIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {/* Optional: More Details */}
      <details className="rounded-lg border border-slate-200 px-3 py-2">
        <summary className="text-sm font-medium text-slate-700 cursor-pointer">More Details</summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Financing">
            <select className={inputCls} value={value.financingType || ""} onChange={set("financingType")}>
              <option value="">—</option>
              {FINANCING_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Min Sq Ft">
            <input type="number" className={inputCls} value={value.sqftMin ?? ""} onChange={setNum("sqftMin")} placeholder="1400" />
          </Field>
          <div className="col-span-2">
            <Field label="Must-Haves (comma-separated)">
              <CsvInput value={value.mustHaves} onChange={(mustHaves) => onChange({ mustHaves })} placeholder="Garage, Yard, Single-story" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Motivation">
              <input type="text" className={inputCls} value={value.motivation || ""} onChange={set("motivation")} placeholder="Upsizing — growing family" />
            </Field>
          </div>
          <Field label="Must Sell First?">
            <select className={inputCls} value={value.mustSellFirst || ""} onChange={set("mustSellFirst")}>
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Tags (comma-separated)">
              <CsvInput value={value.tags} onChange={(tags) => onChange({ tags })} placeholder="first-timer, cash, referral" />
            </Field>
          </div>
        </div>
      </details>

      {/* Follow-up */}
      <div>
        <h4 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">
          Pipeline &amp; Follow-Up
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assigned Agent">
            <select className={inputCls} value={value.assignedAgent || ""} onChange={(e) => {
              const a = agents.find((x) => x.id === e.target.value);
              onChange({
                assignedAgent: e.target.value,
                assignedAgentName: a ? [a.firstname, a.lastname].filter(Boolean).join(" ") : "",
              });
            }}>
              <option value="">—</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {[a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Next Follow-Up">
            <input type="date" className={inputCls} value={value.nextFollowUp || ""} onChange={set("nextFollowUp")} />
          </Field>
          <div className="col-span-2">
            <Field label="Co-Agents — also see & can work this lead">
              {Array.isArray(value.coAgents) && value.coAgents.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {value.coAgents.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1 bg-slate-50 text-linkColor rounded-full px-2 py-0.5 text-xs font-medium">
                      {c.name || c.id}
                      <button
                        type="button"
                        className="text-linkColor hover:text-themeColor"
                        onClick={() =>
                          onChange({ coAgents: (value.coAgents || []).filter((x) => x.id !== c.id) })
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select
                className={inputCls}
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  const a = agents.find((x) => x.id === id);
                  if (!a) return;
                  const name = [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id;
                  const existing = Array.isArray(value.coAgents) ? value.coAgents : [];
                  if (existing.some((x) => x.id === id)) return;
                  onChange({ coAgents: [...existing, { id, name }] });
                }}
              >
                <option value="">+ Add co-agent…</option>
                {agents
                  .filter(
                    (a) =>
                      a.id !== value.assignedAgent &&
                      !(value.coAgents || []).some((c) => c.id === a.id),
                  )
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {[a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id}
                    </option>
                  ))}
              </select>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Notes">
              <textarea rows={3} className={inputCls} value={value.notes || ""} onChange={set("notes")} placeholder="Liked 88 Maple but kitchen too small…" />
            </Field>
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={value.fubSync === true}
                onChange={(e) => onChange({ fubSync: e.target.checked })}
              />
              Sync to Follow Up Boss
              <span className="text-xs text-slate-400">
                (off = this lead stays out of FUB)
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
